/**
 * AL AGREGAR UN NUEVO PRODUCTO/CATEGORÍA al carrito comercial hay que actualizar 3 lugares:
 *
 * 1. Líneas 14 y 15: agregar la nueva categoría en los tipos SectionCategoryKeys y Subcategory.
 * 2. Constante SUBCATEGORY_VIEW_CONFIG (línea 37): agregar la entrada con icon, colors, quantity y price.
 * 3. Constante SECTIONS_CONFIG (línea 75): agregar una nueva sección o incluir la categoría en categoryKeys
 *    de una sección existente.
 *
 * Opcional: si el producto tiene reglas de exclusión (ej. "si seleccionan X no pueden Y"), actualizar
 * SUBCATEGORY_DISABLE_RULES más abajo en este archivo.
 */

// Tipos para el config (si lo sacas a constantes, define esto también)
export type SectionCategoryKeys = ('premium' | 'Destacado' | 'elite' | 'Leads' | 'smart_leads')[] ;
export type Subcategory = 'destacado' | 'prime' | 'leads' | 'smart_leads' | 'premium' | 'elite';

/** Subcategorías sin input de cantidad en el card (solo precio y/o zona Elite). La cantidad en Redux sigue siendo el default al seleccionar. */
export const SUBCATEGORIES_WITHOUT_QUANTITY_INPUT = new Set<Subcategory>(['premium', 'elite']);

/**
 * Subcategorías donde el precio total se calcula como (precio mínimo unitario × cantidad)
 * al cambiar la cantidad; el mínimo del input precio sigue siendo ese total.
 * El unitario sale de `SUBCATEGORY_VIEW_CONFIG[*].price.min` (destacado/prime: 60; smart_leads: 100).
 */
export const SUBCATEGORIES_WITH_PRICE_SCALED_BY_QUANTITY = new Set<Subcategory>(['destacado', 'prime', 'smart_leads']);

export type SectionColors = {
    borderColor: string;
    backgroundColor: string;
    checkColor: string;
};

/** Min, max y valor por defecto para cantidad y precio por subcategoría (inputs del selector). */
export type SubcategoryRangeConfig = {
    quantity: { min: number; max: number; default: number };
    price: { min: number; max: number; default: number };
};

export type SubcategoryViewConfigEntry = {
    icon: string;
    colors: SectionColors;
    quantity: SubcategoryRangeConfig['quantity'];
    price: SubcategoryRangeConfig['price'];
};

/** Icono, colores y rangos (min/max/default) por subcategoría. Si la subcategoría no está, se usa el de la sección. */
export const SUBCATEGORY_VIEW_CONFIG: Partial<Record<Subcategory, SubcategoryViewConfigEntry>> = {
    premium: {
        icon: 'pcom-icon-bolt',
        colors: { borderColor: '#1B4598', backgroundColor: '#F3F6FA', checkColor: '#1B4598' },
        quantity: { min: 1, max: 1, default: 1 },
        price: { min: 499, max: 999999, default: 499 },
    },
    destacado: {
        icon: 'pcom-icon-rate-empty',
        colors: { borderColor: '#E9B44A', backgroundColor: '#FDFBF6', checkColor: '#E9B44A' },
        quantity: { min: 1, max: 600, default: 1 },
        price: { min: 60, max: 999999, default: 60 },
    },
    prime: {
        icon: 'pcom-icon-rate-full',
        colors: { borderColor: '#3B82F6', backgroundColor: '#EFF6FF', checkColor: '#3B82F6' },
        quantity: { min: 1, max: 600, default: 1 },
        price: { min: 60, max: 999999, default: 60 },
    },
    leads: {
        icon: 'pcom-icon-whatsapp',
        colors: { borderColor: '#008057', backgroundColor: '#F4F9F7', checkColor: '#008057' },
        quantity: { min: 999999, max: 999999, default: 999999 },
        price: { min: 200, max: 999999, default: 200 },
    },
    smart_leads: {
        icon: 'pcom-icon-message-outline',
        colors: { borderColor: '#0EA5E9', backgroundColor: '#F0F9FF', checkColor: '#0EA5E9' },
        quantity: { min: 1, max: 20, default: 1 },
        price: { min: 100, max: 999999, default: 100 },
    },
    elite: {
        icon: 'pcom-icon-HomeSearch',
        colors: { borderColor: '#008057', backgroundColor: '#F4F9F7', checkColor: '#008057' },
        quantity: { min: 1, max: 1, default: 1 },
        price: { min: 500, max: 999999, default: 500 },
    },
};

/**
 * Configuración solo de VISTA: orden de secciones, títulos y qué categorías del API
 * muestra cada una. No incluye reglas de negocio (ej. "si seleccionan X no pueden Y");
 * esas van en SECTION_DISABLE_RULES o en getDisabledSectionIds().
 */
export const SECTIONS_CONFIG: {
    sectionId: string;
    title: string;
    categoryKeys: SectionCategoryKeys;
    icon: string;
    colors: {
        borderColor: string;
        backgroundColor: string;
        checkColor: string;
    };
}[] = [
        {
            sectionId: 'premium',
            title: 'ANUNCIO SIMPLE',
            categoryKeys: ['premium'],
            icon: 'pcom-icon-bolt',
            colors:{
                borderColor: '#1B4598',
                backgroundColor: '#F3F6FA',
                checkColor: '#1B4598',
            }
        },
        {
            sectionId: 'destacado',
            title: 'MEJORA LA VISIBILIDAD',
            categoryKeys: ['Destacado', 'elite'],
            icon: 'pcom-icon-rate-empty',
            colors:{
                borderColor: '#E9B44A',
                backgroundColor: '#FDFBF6',
                checkColor: '#E9B44A',
            }
        },
        {
            sectionId: 'leads',
            title: 'CAPTACIÓN Y CALIDAD DE LEADS',
            categoryKeys: ['Leads', 'smart_leads'],
            icon: 'pcom-icon-whatsapp',
            colors:{
                borderColor: '#008057',
                backgroundColor: '#F4F9F7',
                checkColor: '#008057',
            }
        },
    ];

/** Orden de categorías para mostrar (mismo que en el selector: Premium → Destacado → Leads → Smart Leads). */
export const ORDERED_CATEGORIES_FOR_DISPLAY: readonly string[] = SECTIONS_CONFIG.flatMap(
    (s) => s.categoryKeys
);

/** Claves usadas en las 3 primeras secciones; "otros" = el resto */
export const KNOWN_SECTION_KEYS: Set<string> = new Set([
    'premium',
    'Destacado',
    'elite',
    'Leads',
    'smart_leads',
]);

/**
 * Reglas de negocio: cuando esta subcategoría está seleccionada,
 * las subcategorías listadas no se pueden seleccionar (y se limpian si ya estaban).
 */
export const SUBCATEGORY_DISABLE_RULES: Partial<Record<Subcategory, Subcategory[]>> = {
    premium: ['prime', 'smart_leads', 'elite'],
    leads: ['elite'],
    smart_leads: ['elite'],
    elite: ['leads', 'smart_leads', 'premium'],
};

/** Regla de auto-selección con overrides opcionales de quantity y price. */
export type AutoSelectRule = {
    subcategory: Subcategory;
    /** Si se define, sobreescribe quantity { min, max, default } del SUBCATEGORY_VIEW_CONFIG. */
    quantity?: { min: number; max: number; default: number };
    /** Si se define, sobreescribe price { min, max, default } del SUBCATEGORY_VIEW_CONFIG. */
    price?: { min: number; max: number; default: number };
};

/**
 * Reglas de auto-selección: cuando se selecciona una subcategoría,
 * los productos de las subcategorías listadas se seleccionan automáticamente
 * (si no lo estaban ya). Permite sobreescribir rangos de quantity/price.
 * Ejemplo: al seleccionar "premium" se auto-selecciona "leads" con precio en 0.
 */
export const SUBCATEGORY_AUTO_SELECT_RULES: Partial<Record<Subcategory, AutoSelectRule[]>> = {};

/** Zonas disponibles para el producto Elite con su precio mínimo correspondiente. */
export const ELITE_ZONE_PRICES: { label: string; minPrice: number }[] = [
    { label: 'DF/CDMX', minPrice: 4000 },
    { label: 'Estado de México', minPrice: 4000 },
    { label: 'Jalisco', minPrice: 2000 },
    { label: 'Nuevo León', minPrice: 2000 },
    { label: 'Querétaro', minPrice: 1500 },
];